import { HttpClient, HttpParams } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { TransactionMetadataConfig } from '../models/transaction-metadata.model'
import TRANSACTION_METADATA_CONFIG from '../registry/catalogs/transaction-metadata.config.json'
import { adaptTransactionMetadata } from './transaction-metadata.adapter'
import { TransactionMetadataSource } from './transaction-metadata.service'

/** The document as the endpoint sends it; typed only as far as the acceptance checks need. */
interface RawMetadataDocument {
  tranCategories?: { loadoutCondition?: unknown }[]
  fields?: unknown[]
}

/** Whether a served document can actually open a dialog. */
const canOpenADialog = (document?: RawMetadataDocument): boolean =>
  !!document?.fields?.length &&
  !!document.tranCategories?.length &&
  document.tranCategories.some(category => category.loadoutCondition != null)

/**
 * The flex-transactions pass-through added by helios PVAL-26478, which relays
 * recon-portfolio-service's `GET /api/v1/transaction-dialog-metadata?source=` unreshaped.
 *
 * With `<base href="/helios/">` this resolves to
 * `/helios/helios-flex-transactions/transactions/dialog-metadata`. flex-transactions lives on its
 * own host, so for local `yarn start` against beta a `/helios/helios-flex-transactions/**` proxy
 * entry must exist AHEAD of the generic `/helios/helios-*` catch-all — the same LOCAL-ONLY edit the
 * field catalog needs, and the same one CLAUDE.md requires reverting before committing.
 */
export const TRANSACTION_METADATA_URL = `helios-flex-transactions/transactions/dialog-metadata`

/**
 * `true` serves the dialog from the live endpoint; `false` uses the checked-in catalogue.
 *
 * Currently `true`, to exercise the endpoint end to end. Two things are known to be behind it: the
 * helios route ships on PVAL-26478 and is not on master yet, and RPS's seed is stale against the
 * checked-in catalogue. Neither is fatal — a failed fetch keeps the catalogue and logs — but a
 * SUCCESSFUL fetch of a stale document silently serves stale config, which looks like nothing is
 * wrong. Flip back to `false` if the dialog starts disagreeing with the catalogue.
 *
 * Annotated `boolean` rather than the inferred literal so neither branch is narrowed away as dead
 * code.
 */
// The annotation is the point: without it TypeScript narrows this to the literal `false`, which
// makes every consumer's other branch unreachable and turns flipping the switch into a type error.
// The directive must sit immediately above the declaration -- a wrapped reason in between would
// disable the rule on the comment instead.
// eslint-disable-next-line @typescript-eslint/no-inferrable-types
export const USE_HTTP_TRANSACTION_METADATA: boolean = true

/**
 * Serves the dialog metadata from the endpoint, falling back to the checked-in catalogue.
 *
 * {@link TransactionMetadataSource.config} is synchronous, so the document is held in a field that
 * starts as the checked-in catalogue and is replaced once the fetch resolves — no consumer changes.
 * The consequence: a dialog opened in the first few hundred milliseconds renders the catalogue for
 * that one open and does not refresh itself.
 *
 * A failed fetch leaves the catalogue in place so the dialog still opens, which is indistinguishable
 * to the user from stale config — hence logged rather than swallowed.
 */
@Injectable()
export class HttpTransactionMetadataService extends TransactionMetadataSource {
  private current = adaptTransactionMetadata(TRANSACTION_METADATA_CONFIG)

  constructor(private readonly http: HttpClient) {
    super()
    this.load()
  }

  config(): TransactionMetadataConfig {
    return this.current
  }

  private load(): void {
    this.http
      .get<RawMetadataDocument>(TRANSACTION_METADATA_URL, {
        params: new HttpParams().set('source', this.source),
      })
      .subscribe({
        next: document => {
          if (!canOpenADialog(document)) {
            console.error(
              '[transactions-2] transaction metadata document cannot open a dialog ' +
                '(no fields, no tran categories, or no category carrying a loadoutCondition); ' +
                'serving the checked-in catalogue',
              {
                fields: document?.fields?.length ?? 0,
                tranCategories: document?.tranCategories?.length ?? 0,
                withLoadoutCondition:
                  document?.tranCategories?.filter(
                    category => category.loadoutCondition != null,
                  ).length ?? 0,
              },
            )
            return
          }
          this.current = adaptTransactionMetadata(document)
        },
        // 400 = source unrecognised, 404 = source recognised but nothing seeded, else upstream.
        error: (error: unknown) => {
          console.error(
            '[transactions-2] transaction metadata fetch failed; serving the checked-in catalogue',
            error,
          )
        },
      })
  }
}
